import * as i0 from '@angular/core';
import { inject, Injectable, Directive, ElementRef, Renderer2, EventEmitter, TemplateRef, Component, ChangeDetectionStrategy, ContentChild, Input, Output, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import { BehaviorSubject, map } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import * as i2 from '@angular/material/slide-toggle';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

class ThemeSwitcherService {
    setThemeValue(theme) {
        this.setTheme(theme);
        this.themeValue$.next(theme);
    }
    toggleTheme() {
        const currentTheme = this.getLocalStorageTheme();
        const newTheme = currentTheme === 'dark-theme' ? 'light-theme' : 'dark-theme';
        this.setTheme(newTheme);
        this.themeValue$.next(newTheme);
    }
    constructor() {
        this.document = inject(DOCUMENT);
        this._localStorageThemeKey = 'titan-user-theme';
        /* Source of truth for theme value is stored in localStorage */
        this.themeValue$ = new BehaviorSubject(this.getLocalStorageTheme());
        this.themeToggleChecked$ = this.themeValue$.pipe(map(theme => theme === 'light-theme'));
        this.setTheme(this.getLocalStorageTheme());
    }
    getLocalStorageTheme() {
        return this.document.defaultView?.localStorage.getItem(this._localStorageThemeKey) ?? 'dark-theme';
    }
    setLocalStorageTheme(theme) {
        this.document.defaultView?.localStorage.setItem(this._localStorageThemeKey, theme);
    }
    setTheme(theme) {
        if (theme === 'light-theme') {
            this.document.body.classList.remove('dark-theme');
            this.document.body.classList.add(theme);
            this.setLocalStorageTheme(theme);
        }
        else {
            this.document.body.classList.remove('light-theme');
            this.document.body.classList.add(theme);
            this.setLocalStorageTheme(theme);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ThemeSwitcherService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ThemeSwitcherService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ThemeSwitcherService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

class TitanThemeToggleLeftDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanThemeToggleLeftDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.2", type: TitanThemeToggleLeftDirective, isStandalone: true, selector: "[titanThemeToggleLeft]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanThemeToggleLeftDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[titanThemeToggleLeft]',
                }]
        }] });

class TitanThemeToggleRightDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanThemeToggleRightDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.2", type: TitanThemeToggleRightDirective, isStandalone: true, selector: "[titanThemeToggleRight]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanThemeToggleRightDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[titanThemeToggleRight]',
                }]
        }] });

/* Angular */
/**
 * Directive used on mat-slide-toggle elements to enable switch icons.
 */
class ToggleIconDirective {
    constructor() {
        this.el = inject(ElementRef);
        this.renderer = inject(Renderer2);
        this.renderer.addClass(this.el.nativeElement, 'titan-switch-enabled');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ToggleIconDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.2", type: ToggleIconDirective, isStandalone: true, selector: "[enableToggleIcon]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ToggleIconDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[enableToggleIcon]',
                }]
        }], ctorParameters: () => [] });

/* Angular */
class TitanThemeToggleComponent {
    constructor() {
        this.themeSwitcher = inject(ThemeSwitcherService);
        this.enableSwitchIcon = false;
        this.themeChanged = new EventEmitter();
    }
    onToggleClick(event) {
        event.stopPropagation();
        this.themeChanged.emit();
    }
    themeToggleChanged($event) {
        this.themeSwitcher.toggleTheme();
        this.themeChanged.emit($event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanThemeToggleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.2", type: TitanThemeToggleComponent, isStandalone: true, selector: "titan-theme-toggle", inputs: { toggleBarColor: "toggleBarColor", enableSwitchIcon: "enableSwitchIcon" }, outputs: { themeChanged: "themeChanged" }, queries: [{ propertyName: "themeToggleLeftTemplateRef", first: true, predicate: TitanThemeToggleLeftDirective, descendants: true, read: TemplateRef }, { propertyName: "themeToggleRightTemplateRef", first: true, predicate: TitanThemeToggleRightDirective, descendants: true, read: TemplateRef }], ngImport: i0, template: "<div class=\"theme-toggle-container\">\n    <div class=\"theme-toggle-container-left\">\n        <ng-container *ngTemplateOutlet=\"themeToggleLeftTemplateRef\"></ng-container>\n    </div>\n    <mat-slide-toggle\n        #switchToggle\n        [ngClass]=\"enableSwitchIcon ? 'titan-switch-enabled' : ''\"\n        data-testid=\"titan-theme-toggle-color\"\n        aria-label=\"Toggle Light and Dark Theme\"\n        (click)=\"onToggleClick($event)\"\n        [color]=\"toggleBarColor ? toggleBarColor : 'primary'\"\n        [checked]=\"themeSwitcher.themeToggleChecked$ | async\"\n        [disableRipple]=\"true\">\n    </mat-slide-toggle>\n    <div class=\"theme-toggle-container-right\">\n        <ng-container *ngTemplateOutlet=\"themeToggleRightTemplateRef\"></ng-container>\n    </div>\n</div>\n", styles: [":host-context(.light-theme) .theme-toggle-container-left{color:var(--btq-theme-toggle-icons-lighter);opacity:.5;padding-right:4px}:host-context(.light-theme) .theme-toggle-container-right{color:var(--btq-theme-toggle-icons-darker)}:host-context(.dark-theme) .theme-toggle-container-left{color:var(--btq-theme-toggle-icons-lighter);padding-right:4px}:host-context(.dark-theme) .theme-toggle-container-right{color:var(--btq-theme-toggle-icons-darker);opacity:.5}.theme-toggle-container{display:flex;flex-direction:row;align-items:center;padding:0}.theme-toggle-container-left,.theme-toggle-container-right{display:flex;transform:scale(.6)}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "ngmodule", type: MatIconModule }, { kind: "ngmodule", type: MatSlideToggleModule }, { kind: "component", type: i2.MatSlideToggle, selector: "mat-slide-toggle", inputs: ["disabled", "disableRipple", "color", "tabIndex", "name", "id", "labelPosition", "aria-label", "aria-labelledby", "aria-describedby", "required", "checked", "hideIcon"], outputs: ["change", "toggleChange"], exportAs: ["matSlideToggle"] }, { kind: "ngmodule", type: FormsModule }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanThemeToggleComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'titan-theme-toggle', imports: [
                        CommonModule,
                        MatIconModule,
                        MatSlideToggleModule,
                        FormsModule,
                        TitanThemeToggleLeftDirective,
                        TitanThemeToggleRightDirective,
                        ToggleIconDirective,
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"theme-toggle-container\">\n    <div class=\"theme-toggle-container-left\">\n        <ng-container *ngTemplateOutlet=\"themeToggleLeftTemplateRef\"></ng-container>\n    </div>\n    <mat-slide-toggle\n        #switchToggle\n        [ngClass]=\"enableSwitchIcon ? 'titan-switch-enabled' : ''\"\n        data-testid=\"titan-theme-toggle-color\"\n        aria-label=\"Toggle Light and Dark Theme\"\n        (click)=\"onToggleClick($event)\"\n        [color]=\"toggleBarColor ? toggleBarColor : 'primary'\"\n        [checked]=\"themeSwitcher.themeToggleChecked$ | async\"\n        [disableRipple]=\"true\">\n    </mat-slide-toggle>\n    <div class=\"theme-toggle-container-right\">\n        <ng-container *ngTemplateOutlet=\"themeToggleRightTemplateRef\"></ng-container>\n    </div>\n</div>\n", styles: [":host-context(.light-theme) .theme-toggle-container-left{color:var(--btq-theme-toggle-icons-lighter);opacity:.5;padding-right:4px}:host-context(.light-theme) .theme-toggle-container-right{color:var(--btq-theme-toggle-icons-darker)}:host-context(.dark-theme) .theme-toggle-container-left{color:var(--btq-theme-toggle-icons-lighter);padding-right:4px}:host-context(.dark-theme) .theme-toggle-container-right{color:var(--btq-theme-toggle-icons-darker);opacity:.5}.theme-toggle-container{display:flex;flex-direction:row;align-items:center;padding:0}.theme-toggle-container-left,.theme-toggle-container-right{display:flex;transform:scale(.6)}\n"] }]
        }], propDecorators: { themeToggleLeftTemplateRef: [{
                type: ContentChild,
                args: [TitanThemeToggleLeftDirective, { read: TemplateRef }]
            }], themeToggleRightTemplateRef: [{
                type: ContentChild,
                args: [TitanThemeToggleRightDirective, { read: TemplateRef }]
            }], toggleBarColor: [{
                type: Input
            }], enableSwitchIcon: [{
                type: Input
            }], themeChanged: [{
                type: Output
            }] } });

/* Module */
class TitanThemeToggleModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanThemeToggleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: TitanThemeToggleModule, imports: [CommonModule, TitanThemeToggleComponent, TitanThemeToggleLeftDirective, TitanThemeToggleRightDirective], exports: [TitanThemeToggleComponent, TitanThemeToggleLeftDirective, TitanThemeToggleRightDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanThemeToggleModule, imports: [CommonModule, TitanThemeToggleComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanThemeToggleModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TitanThemeToggleComponent, TitanThemeToggleLeftDirective, TitanThemeToggleRightDirective],
                    exports: [TitanThemeToggleComponent, TitanThemeToggleLeftDirective, TitanThemeToggleRightDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ThemeSwitcherService, TitanThemeToggleComponent, TitanThemeToggleLeftDirective, TitanThemeToggleModule, TitanThemeToggleRightDirective, ToggleIconDirective };
